import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { 
  Download, Mail, Calendar, Euro, Building, 
  User, FileText, CheckCircle, ArrowLeft, Loader2
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevisPage = () => {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const quoteRef = useRef(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API}/quotes/${quoteId}`);
        setQuote(response.data);
      } catch (error) {
        console.error("Error fetching quote:", error);
      } finally {
        setLoading(false);
      }
    };

    if (quoteId) {
      fetchQuote();
    }
  }, [quoteId]);

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const downloadPDF = async () => {
    if (!quoteRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(quoteRef.current, {
        scale: 2,
        backgroundColor: '#0F0F10',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Devis_Proforma_${quote.quote_number}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      // Fallback to print
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="devis-not-found">
        <Navigation />
        <div className="pt-32 pb-24 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Devis non trouvé</h1>
          <p className="text-white/60 mb-8">Ce devis n'existe pas ou a expiré.</p>
          <Link to="/tunnel" className="btn-primary">
            Demander un nouveau devis
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="devis-page">
      <Navigation />

      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6">
          {/* Success Banner */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 mb-8 border-green-500/20 bg-green-500/5"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Votre devis proforma est prêt !</h2>
                <p className="text-white/60">
                  Un email récapitulatif va vous être envoyé à {quote.email}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Quote Document */}
          <motion.div
            ref={quoteRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card overflow-hidden bg-[#0F0F10]"
          >
            {/* Header */}
            <div className="p-8 border-b border-white/10 bg-gradient-to-r from-[#007AFF]/10 to-transparent">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">DEVIS PROFORMA</h1>
                  <p className="text-[#007AFF] font-mono text-lg">{quote.quote_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">Académie IA</p>
                  <p className="text-white/70 text-sm">Formation Intelligence Artificielle</p>
                  <p className="text-white/50 text-sm">academie-ia.fr</p>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="p-8 border-b border-white/10">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-white/50 text-sm mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    CLIENT
                  </h3>
                  <p className="text-xl font-semibold text-white">{quote.company_name}</p>
                  <p className="text-white/70">{quote.contact_name}</p>
                  <p className="text-white/50">{quote.email}</p>
                </div>
                <div>
                  <h3 className="text-white/50 text-sm mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    DATES
                  </h3>
                  <p className="text-white/70">
                    <span className="text-white/50">Émis le:</span> {formatDate(quote.created_at)}
                  </p>
                  <p className="text-white/70">
                    <span className="text-white/50">Valide jusqu'au:</span> {formatDate(quote.valid_until)}
                  </p>
                </div>
              </div>
            </div>

            {/* Quote Details */}
            <div className="p-8 border-b border-white/10">
              <h3 className="text-white/50 text-sm mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                DESCRIPTION
              </h3>
              <div className="bg-white/5 rounded-xl p-6">
                <pre className="text-white/80 whitespace-pre-wrap font-sans leading-relaxed">
                  {quote.description}
                </pre>
              </div>
            </div>

            {/* Pricing - TVA non applicable */}
            <div className="p-8">
              <h3 className="text-white/50 text-sm mb-4 flex items-center gap-2">
                <Euro className="w-4 h-4" />
                TARIFICATION
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Formation - Tarif journalier</p>
                    <p className="text-white/50 text-sm">{quote.days_count} jour(s) × {quote.participant_count} participants</p>
                  </div>
                  <p className="text-white font-mono">{formatCurrency(quote.total_ht)}</p>
                </div>

                <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Frais de déplacement et hébergement</p>
                    <p className="text-white/50 text-sm">Au réel, au départ de Versailles (IDF)</p>
                  </div>
                  <p className="text-white/60 font-mono italic">Sur justificatifs</p>
                </div>

                <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg">
                  <p className="text-white/50">TVA</p>
                  <p className="text-white/60 font-mono">Non applicable</p>
                </div>

                <div className="flex justify-between items-center p-4 bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-lg">
                  <p className="text-white font-semibold">Total</p>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#007AFF] font-mono">{formatCurrency(quote.total_ht)}</p>
                    <p className="text-white/50 text-xs">+ frais de déplacement</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="p-8 bg-white/5 border-t border-white/10">
              <p className="text-white/60 text-sm mb-4">
                <strong className="text-white/80">Conditions de règlement :</strong><br />
                Acompte de 30% à la commande. Solde à réception de la facture après formation.
              </p>
              <p className="text-white/40 text-xs">
                TVA non applicable, article 293 B du CGI.
              </p>
            </div>

            {/* Legal mentions */}
            <div className="p-8 border-t border-white/10 bg-[#050505]">
              <div className="text-center text-white/40 text-xs space-y-1">
                <p className="font-semibold text-white/60">LE POOL PRESSE SAS</p>
                <p>Organisme de formation professionnelle</p>
                <p>28240 Fontaine-Simon</p>
                <p className="mt-2">academie-ia.fr • contact@academie-ia.fr</p>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <div className="flex flex-wrap gap-4 mt-8">
            <Link
              to="/"
              className="btn-secondary flex items-center gap-2"
              data-testid="btn-back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="btn-primary flex items-center gap-2"
              data-testid="btn-download-pdf"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {downloading ? "Génération..." : "Télécharger le PDF"}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DevisPage;
